<template>
  <FormPage :title="pageTitle" :description="pageDescription">
    <template #controls>
      <button class="btn" type="button" @click="goToHub">
        <font-awesome-icon icon="fa-solid fa-arrow-left" /> Назад к исполнителю
      </button>
    </template>

    <my-loader v-if="isLoading" />

    <div v-else-if="!executor" class="empty-state">
      Исполнитель не найден.
      <button class="btn" type="button" @click="goToHub">Вернуться к хабу</button>
    </div>

    <template v-else>
      <div v-if="step === 'company'">
        <CompanyForm
          v-if="executor.company"
          mode="edit"
          :initial-data="executor.company"
          :errors="companyErrors"
          @update="saveCompany"
          @closeForm="goToHub"
        />
        <FormCard v-else title="Данные компании">
          <p class="preview-empty">Компания не назначена. Назначьте существующую через кнопку «Заменить» на странице исполнителя.</p>
          <button type="button" class="hint-btn" @click="goToHub">Вернуться к исполнителю</button>
        </FormCard>

        <FormCard v-if="executor.company" title="Безопасность компании">
          <CompanySecurityInfo
            :security="companySecurity"
            :companyId="executor.company.ati_id"
          />
          <div class="security-actions">
            <guard-button
              v-if="accessShowCompany"
              :model="'companies'"
              :id="executor.company.id"
              @reloadExecutor="reloadExecutor"
            />
            <span class="security-hint">Повторная проверка ИНН/АТИ</span>
          </div>
        </FormCard>

        <FormCard v-if="executor.company" title="Банковские реквизиты">
          <BankDetail
            :key="bankKey"
            :company="executor.company"
            :executor="executor"
            @UpdateBank="onUpdateBank"
            @DeleteBank="onDeleteBank"
            @AddBank="onAddBank"
          />
        </FormCard>
      </div>

      <div v-else-if="step === 'manager'">
        <ManagerForm
          v-if="executor.manager"
          mode="edit"
          :initial-data="executor.manager"
          :errors="managerErrors"
          @update="onEntityUpdate"
        />
        <FormCard v-else title="Данные менеджера">
          <p class="preview-empty">Менеджер не назначен. Назначьте существующего через кнопку «Заменить» на странице исполнителя.</p>
          <button type="button" class="hint-btn" @click="goToHub">Вернуться к исполнителю</button>
        </FormCard>

        <FormCard v-if="executor.manager" title="Безопасность менеджера">
          <ManagerSecurityInfo :security="managerSecurity" />
          <div class="security-actions">
            <guard-button
              v-if="accessShowManager"
              :model="'managers'"
              :id="executor.manager.id"
              @reloadExecutor="reloadExecutor"
            />
            <span class="security-hint">Повторная проверка АТИ</span>
          </div>
        </FormCard>

        <ExecutorWarning
          v-if="managerWarningErrors.length"
          :errors="managerWarningErrors"
          :key="warningKey"
          @confirm="confirmManager"
        />
      </div>

      <div v-else-if="step === 'driver'">
        <DriverInfo
          v-if="executor.driver"
          :driver="executor.driver"
          @updateDriver="onDriverUpdate"
        />
        <FormCard v-else title="Данные водителя">
          <p class="preview-empty">Водитель не назначен. Назначьте существующего через кнопку «Заменить» на странице исполнителя.</p>
          <button type="button" class="hint-btn" @click="goToHub">Вернуться к исполнителю</button>
        </FormCard>

        <FormCard v-if="executor.driver" title="Безопасность водителя">
          <DriverSecurityInfo :security="driverSecurity" />
          <div class="security-actions">
            <guard-button
              v-if="accessShowDriver"
              :model="'drivers'"
              :id="executor.driver.id"
              @reloadExecutor="reloadExecutor"
            />
            <span class="security-hint">Повторная проверка ИНН/паспорта</span>
          </div>
        </FormCard>
      </div>

      <div v-else-if="step === 'car'">
        <CarForm
          v-if="executor.car"
          mode="edit"
          :no-trailer="true"
          :initial-data="executor.car"
          :errors="carErrors"
          @update="onEntityUpdate"
        />
        <FormCard v-else title="Данные авто">
          <p class="preview-empty">Автомобиль не назначен. Назначьте существующий через кнопку «Заменить» на странице исполнителя.</p>
          <button type="button" class="hint-btn" @click="goToHub">Вернуться к исполнителю</button>
        </FormCard>

        <FormCard v-if="executor.car" title="Безопасность авто">
          <CarSecurityInfo
            :security="carSecurity"
            :car="{ vin: executor.car.vin, number: executor.car.number }"
          />
          <div class="security-actions">
            <guard-button
              v-if="accessShowCar"
              :model="'cars'"
              :id="executor.car.id"
              @reloadExecutor="reloadExecutor"
            />
            <span class="security-hint">Повторная проверка avtocod</span>
          </div>
        </FormCard>
      </div>

      <div v-else-if="step === 'trailer'">
        <TrailerForm
          v-if="executor.trailer"
          mode="edit"
          :initial-data="executor.trailer"
          :errors="trailerErrors"
          @update="onEntityUpdate"
        />
        <FormCard v-else title="Данные прицепа">
          <p class="preview-empty">Прицеп не назначен. Назначьте существующий через кнопку «Заменить» на странице исполнителя.</p>
          <button type="button" class="hint-btn" @click="goToHub">Вернуться к исполнителю</button>
        </FormCard>

        <FormCard v-if="executor.trailer" title="Безопасность прицепа">
          <CarSecurityInfo
            :security="trailerSecurity"
            :car="{ vin: executor.trailer.vin, number: executor.trailer.number }"
          />
          <div class="security-actions">
            <guard-button
              v-if="accessShowTrailer"
              :model="'cars'"
              :id="executor.trailer.id"
              @reloadExecutor="reloadExecutor"
            />
            <span class="security-hint">Повторная проверка avtocod</span>
          </div>
        </FormCard>
      </div>

      <div v-else class="unknown-step">
        <p>Неизвестный шаг: {{ step }}</p>
        <button class="btn" type="button" @click="goToHub">Вернуться к хабу</button>
      </div>
    </template>
  </FormPage>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { createToaster } from '@meforma/vue-toaster'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'
import ManagerForm from '@/components/forms/ManagerForm.vue'
import CarForm from '@/components/forms/CarForm.vue'
import TrailerForm from '@/components/forms/TrailerForm.vue'
import CompanyForm from '@/components/forms/CompanyForm.vue'
import DriverInfo from '@/pages/securityCheck/components/DriverInfo.vue'
import BankDetail from '@/pages/securityCheck/components/BankDetail.vue'
import CompanySecurityInfo from '@/pages/securityCheck/components/CompanySecurityInfo.vue'
import ManagerSecurityInfo from '@/pages/securityCheck/components/ManagerSecurityInfo.vue'
import DriverSecurityInfo from '@/pages/securityCheck/components/DriverSecurityInfo.vue'
import CarSecurityInfo from '@/pages/securityCheck/components/CarSecurityInfo.vue'
import ExecutorWarning from '@/pages/securityCheck/components/warning/ExecutorWarning.vue'

const STEP_LABELS = {
  company: 'Компания',
  manager: 'Менеджер',
  driver: 'Водитель',
  car: 'Автомобиль',
  trailer: 'Прицеп',
}

export default {
  name: 'ExecutorDetailStep',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
    ManagerForm,
    CarForm,
    TrailerForm,
    CompanyForm,
    DriverInfo,
    BankDetail,
    CompanySecurityInfo,
    ManagerSecurityInfo,
    DriverSecurityInfo,
    CarSecurityInfo,
    ExecutorWarning,
  },
  props: {
    step: { type: String, required: true },
  },
  setup(props) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const toaster = createToaster({ position: 'top-right' })
    const model = 'executors'

    const executorId = computed(() => route.params.id)
    const executor = computed(() => store.state.executors.executors.item.data)
    const isLoading = computed(() => store.state.executors.executors.item.loading)

    const managerErrors = computed(() => store.state.apiStorage.managers.formErrors || {})
    const carErrors = computed(() => store.state.apiStorage.cars.formErrors || {})
    const trailerErrors = computed(() => store.state.apiStorage.cars.formErrors || {})
    const companyErrors = computed(() => store.state.apiStorage.companies.formErrors || {})

    const companySaving = ref(false)
    const bankKey = ref(0)
    const warningKey = ref(0)

    const pageTitle = computed(() =>
      `Исполнитель #${executorId.value} → Редактирование: ${STEP_LABELS[props.step] || 'Неизвестно'}`,
    )
    const pageDescription = computed(() => {
      switch (props.step) {
        case 'company': return 'Редактирование компании, проверка безопасности, банковские реквизиты.'
        case 'manager': return 'Редактирование менеджера и проверка безопасности.'
        case 'driver': return 'Редактирование водителя и проверка безопасности.'
        case 'car': return 'Редактирование автомобиля и проверка безопасности.'
        case 'trailer': return 'Редактирование прицепа и проверка безопасности.'
        default: return ''
      }
    })

    const companySecurity = computed(() => firstSecurity(executor.value && executor.value.company))
    const managerSecurity = computed(() => firstSecurity(executor.value && executor.value.manager))
    const driverSecurity = computed(() => firstSecurity(executor.value && executor.value.driver))
    const carSecurity = computed(() => firstSecurity(executor.value && executor.value.car))
    const trailerSecurity = computed(() => firstSecurity(executor.value && executor.value.trailer))

    const CAR_OWNERSHIP_ERROR_MSG = 'Нет данных о периоде владения автомобилем'
    const managerWarningErrors = computed(() => {
      const errors = executor.value && executor.value.errors
      if (!Array.isArray(errors)) return []
      return errors.filter((e) => {
        const msg = e && typeof e === 'object' ? e.msg : e
        return !String(msg || '').includes(CAR_OWNERSHIP_ERROR_MSG)
      })
    })

    function firstSecurity(entity) {
      if (!entity || !Array.isArray(entity.security_info) || !entity.security_info.length) return null
      return entity.security_info[0] || null
    }

    const accessShowCompany = computed(() => {
      const s = companySecurity.value
      return !!(s && (s.dadata_is_end || s.ati_is_end))
    })
    const accessShowManager = computed(() => {
      const s = managerSecurity.value
      return !!(s && s.ati_is_end)
    })
    const accessShowDriver = computed(() => {
      const s = driverSecurity.value
      const cs = companySecurity.value
      return !!((s && s.dadata_is_end) || (cs && cs.nalog_is_end))
    })
    const accessShowCar = computed(() => {
      const s = carSecurity.value
      return !!(s && s.avtocod_is_end)
    })
    const accessShowTrailer = computed(() => {
      const s = trailerSecurity.value
      return !!(s && s.avtocod_is_end)
    })

    onMounted(async () => {
      await store.dispatch('executors/getItem', { model, id: route.params.id, silence: false })
    })

    function goToHub() {
      router.push({ name: 'ExecutorsDetail', params: { id: String(executorId.value) } })
    }

    async function reloadExecutor() {
      await store.dispatch('executors/getItem', { model, id: route.params.id, silence: false })
      warningKey.value += 1
    }

    async function saveCompany(form) {
      if (companySaving.value) return
      companySaving.value = true
      try {
        const ok = await store.dispatch('apiStorage/updateItem', {
          model: 'companies',
          form,
        })
        if (ok) {
          toaster.success('Компания обновлена')
          await reloadExecutor()
          goToHub()
        }
      } finally {
        companySaving.value = false
      }
    }

    async function onAddBank(form) {
      try {
        await store.dispatch('executors/AddBank', { form, company_id: form.company_id })
        await reloadExecutor()
        bankKey.value += 1
        toaster.success('Банковский реквизит добавлен')
      } catch (err) {
        toaster.error(err?.message || 'Не удалось добавить реквизит')
      }
    }

    async function onUpdateBank(form) {
      try {
        await store.dispatch('executors/UpdateBank', { form, id: form.id })
        await reloadExecutor()
        bankKey.value += 1
        toaster.success('Банковский реквизит обновлён')
      } catch (err) {
        toaster.error(err?.message || 'Не удалось обновить реквизит')
      }
    }

    async function onDeleteBank(id) {
      try {
        await store.dispatch('executors/deleteBank', { id })
        await reloadExecutor()
        bankKey.value += 1
        toaster.success('Банковский реквизит удалён')
      } catch (err) {
        toaster.error(err?.message || 'Не удалось удалить реквизит')
      }
    }

    async function onDriverUpdate(form) {
      const response = await store.dispatch('executors/updateSecure', { form, model: 'drivers' })
      if (response) {
        await store.commit('executors/updateDriver', response)
        toaster.success('Водитель обновлён')
        await reloadExecutor()
        goToHub()
      }
    }

    async function onEntityUpdate(form) {
      const entityModel = props.step === 'manager' ? 'managers' : 'cars'
      const ok = await store.dispatch('apiStorage/updateItem', {
        model: entityModel,
        form,
      })
      if (ok) {
        toaster.success(`${STEP_LABELS[props.step]} обновлён`)
        await reloadExecutor()
        goToHub()
      }
    }

    async function confirmManager(value) {
      const manager = executor.value && executor.value.manager
      if (!manager) return
      try {
        const managerForm = { id: manager.id, relation_company: value }
        await store.dispatch('executors/confirmManager', { managerForm })
        toaster.success('Подтверждение связи менеджера обновлено')
        await reloadExecutor()
      } catch (err) {
        toaster.error(err?.message || 'Не удалось подтвердить менеджера')
      }
    }

    return {
      executorId,
      executor,
      isLoading,
      pageTitle,
      pageDescription,
      managerErrors,
      carErrors,
      trailerErrors,
      companyErrors,
      bankKey,
      warningKey,
      companySecurity,
      managerSecurity,
      driverSecurity,
      carSecurity,
      trailerSecurity,
      managerWarningErrors,
      accessShowCompany,
      accessShowManager,
      accessShowDriver,
      accessShowCar,
      accessShowTrailer,
      goToHub,
      reloadExecutor,
      saveCompany,
      onAddBank,
      onUpdateBank,
      onDeleteBank,
      onDriverUpdate,
      onEntityUpdate,
      confirmManager,
    }
  },
}
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  background-color: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
}

.btn:hover {
  border-color: #2563eb;
  color: #2563eb;
}

.step-actions {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.save-button {
  padding: 10px 20px;
  background-color: #2563eb;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  transition: background-color 0.2s;
}

.save-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.save-button:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.hint-btn {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
}

.hint-btn:hover:not(:disabled) {
  background: #f9fafb;
}

.security-actions {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.security-hint {
  color: #6b7280;
  font-size: 12px;
}

.preview-empty {
  color: #9ca3af;
  font-size: 13px;
  margin: 0 0 12px;
}

.empty-state {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  color: #6b7280;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 16px;
  padding: 32px;
}

.unknown-step {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  color: #b42318;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 16px;
  padding: 32px;
}
</style>
